import React from 'react';
import PropTypes from 'prop-types';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import AssessmentTemplates from '../../../components/accountInvitation/AssessmentTemplates';
import { useFormikContext } from 'formik';

const messages = defineMessages({
  panelTitle: { id: 'accounts.add-client.panel-header', defaultMessage: 'Add Client account' },
  fullName: { id: 'attrs.owner.fullName', defaultMessage: "Owner's full name" },
  email: { id: 'attrs.owner.email', defaultMessage: "Owner's Email" },
  message: { id: 'attrs.owner.message', defaultMessage: 'Message to Owner' },
  plan: { id: 'attrs.credit-plan', defaultMessage: 'Account Credit Plan' },
  clientDetailsStepName: { id: 'accounts.add-client.steps.client-details', defaultMessage: 'Client' },
  assessmentStepName: { id: 'accounts.add-client.steps.assessment', defaultMessage: 'Assessment' },
  surveyStepName: { id: 'accounts.add-client.steps.survey', defaultMessage: 'Survey' },
  reviewStepName: { id: 'accounts.add-client.steps.review', defaultMessage: 'Review' },
  cancel: { id: 'actions.cancel', defaultMessage: 'Cancel' },
  submit: { id: 'actions.submit', defaultMessage: 'Submit' },

  available: { id: 'attrs.available', defaultMessage: 'Available' },
  selected: { id: 'attrs.selected', defaultMessage: 'Selected' },
  interestsCheckbox: {
    id: 'accounts.add-client.assessment-dimensions.interests-text',
    defaultMessage: 'Give access to the Interests section',
  },
});

const AssessmentDimensions = ({
  sections: { behaviorSection, cognitiveSection, interestsSection },
  values: { assessmentDimensions },
}) => {
  const { formatMessage } = useIntl();

  const hasBehaviors = assessmentDimensions.behaviorDimensions && assessmentDimensions.behaviorDimensions.length > 0;
  const hasCognitive = assessmentDimensions.cognitiveDimensions && assessmentDimensions.cognitiveDimensions.length > 0;

  const shouldDisplay = hasBehaviors && hasCognitive && assessmentDimensions.interestsDimensionsArePresent;

  if (!shouldDisplay) {
    return null;
  }

  return (
    <div className="p-grid">
      <div className="p-col-12">
        <FormattedMessage
          id="accounts.add-client.assessment-dimensions.heading"
          defaultMessage="Assign Assessment Dimensions"
          tagName="h1"
        />
      </div>
      {hasBehaviors && (
        <>
          <div className="p-col-12">
            <h1>{behaviorSection.displayName}</h1>
            <ul>
              {assessmentDimensions.behaviorDimensions.map((d) => (
                <li key={d.id}>{d.displayName}</li>
              ))}
            </ul>
          </div>

          <div className="p-col-12">
            <FormattedMessage
              id="accounts.add-client.assessment-dimensions.distortion-title"
              defaultMessage="Distortion"
              tagName="h2"
            />
            <ul>
              {assessmentDimensions.distortionDimensions.map((d) => (
                <li key={d.id}>{d.displayName}</li>
              ))}
            </ul>
          </div>
        </>
      )}
      {hasCognitive && (
        <div className="p-col-12">
          <h1>{cognitiveSection.displayName}</h1>
          <ul>
            {assessmentDimensions.cognitiveDimensions.map((d) => (
              <li key={d.id}>{d.displayName}</li>
            ))}
          </ul>
        </div>
      )}
      {assessmentDimensions.interestsDimensionsArePresent && (
        <div className="p-col-12">
          <h1>{interestsSection.displayName}</h1>
          {formatMessage(messages.interestsCheckbox)}
        </div>
      )}
    </div>
  );
};

AssessmentDimensions.propTypes = {
  sections: PropTypes.objectOf(PropTypes.any).isRequired,
  values: PropTypes.objectOf(PropTypes.any).isRequired,
};

const SurveyTemplates = ({ surveyTemplates }) => {
  const shouldDisplay = surveyTemplates.length > 0;

  if (!shouldDisplay) {
    return null;
  }

  return (
    <>
      <FormattedMessage
        id="accounts.invitation-detail.survey-templates.heading"
        defaultMessage="Survey Templates"
        tagName="h1"
      />
      <ul>
        {surveyTemplates.map(({ node }) => (
          <li key={node.id}>{node.title}</li>
        ))}
      </ul>
    </>
  );
};

SurveyTemplates.propTypes = {
  surveyTemplates: PropTypes.arrayOf(PropTypes.object).isRequired,
};

const SurveyAbilities = ({ surveyAbilities }) => {
  const hasAbilities = surveyAbilities && surveyAbilities.length > 0;

  if (!hasAbilities) {
    return null;
  }

  return (
    <>
      <FormattedMessage
        id="accounts.invitation-detail.survey-competencies.heading"
        defaultMessage="Survey Abilities"
        tagName="h1"
      />
      <ul>
        {surveyAbilities.map((surveyAbility) => (
          <li key={surveyAbility.id}>{surveyAbility.displayName}</li>
        ))}
      </ul>
    </>
  );
};

SurveyAbilities.propTypes = {
  surveyAbilities: PropTypes.arrayOf(PropTypes.object).isRequired,
};

const ReviewStep = ({ isCurrentStep, sections, account }) => {
  const { formatMessage } = useIntl();

  const { values } = useFormikContext();

  if (!isCurrentStep) {
    return null;
  }

  return (
    <>
      <div className="p-col-12">
        <FormattedMessage id="accounts.add-client.review.heading" defaultMessage="Review Invitation" tagName="h1" />
      </div>
      <div className="p-col-12">
        <FormattedMessage
          id="accounts.add-client.steps.client.details"
          defaultMessage="The new account is part of organisation {partnerName}"
          values={{
            partnerName: <strong>{account.displayName}</strong>,
          }}
        />
      </div>
      <div className="p-col-12">
        <div className="details">
          <span className="details-title">{`${formatMessage(messages.fullName)}: `}</span>
          <span>{values.fullName}</span>
        </div>
      </div>
      <div className="p-col-12">
        <div className="details">
          <span className="details-title">{`${formatMessage(messages.email)}: `}</span>
          <span>{values.email}</span>
        </div>
      </div>
      <div className="p-col-12">
        <div className="details">
          <span className="details-title">{`${formatMessage(messages.message)}: `}</span>
          <div className="formatted-text">{values.inviteMessage}</div>
        </div>
      </div>
      <div className="p-col-12">
        <AssessmentTemplates templates={values.assessmentTemplates} />
      </div>
      <div className="p-col-12">
        <AssessmentDimensions values={values} sections={sections} />
      </div>
      <div className="p-col-12">
        <SurveyTemplates surveyTemplates={values.surveyTemplates} />
      </div>
      <div className="p-col-12">
        <SurveyAbilities surveyAbilities={values.surveyAbilities} />
      </div>
    </>
  );
};

ReviewStep.propTypes = {
  isCurrentStep: PropTypes.bool.isRequired,
  account: PropTypes.objectOf(PropTypes.any).isRequired,
  sections: PropTypes.objectOf(PropTypes.any).isRequired,
  values: PropTypes.objectOf(PropTypes.any).isRequired,
};

export default ReviewStep;
